import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Play, Pause, Loader2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceReadButton — tikt om de voorleesstem op een verhaalpagina te starten.
 * - Premium-only (toont 402 → toast met 'Premium')
 * - Cached audio via backend (instant playback bij 2e bezoek)
 * - In autoPlay-mode: bij elke pageNumber-wissel start automatisch het voorlezen
 *   en bij audio-einde wordt de meegegeven onEnded callback aangeroepen.
 */
const VoiceReadButton = ({ text, storyId, pageNumber, autoPlay = false, isPremium = false, onEnded }) => {
  const { token } = useAuth();
  const [state, setState] = useState('idle'); // idle | loading | playing | paused | error
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);

  /** Volledig opruimen: pauzeren, bron leegmaken én ref nullen.
   *  Zonder de null-reset denkt fetchAndPlay later dat een (oude) audio nog
   *  gehervat moet worden, waardoor auto-play niets liet horen. */
  const cleanupAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      try { audioRef.current.src = ''; } catch (e) { /* ignore */ }
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  // Reset zodra pagina/verhaal wisselt
  useEffect(() => {
    return cleanupAudio;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, pageNumber]);

  const fetchAndPlay = async () => {
    if (!isPremium) {
      toast.info('🔊 Voorlezen is een premium-functie. Start je gratis proefperiode!');
      return;
    }
    if (!text || !text.trim()) return;

    // Hervatten alleen wanneer er nog steeds een geldige audio loopt voor
    // exact dezelfde pagina — anders altijd een verse fetch om verkeerde
    // playback te voorkomen.
    const a = audioRef.current;
    if (a && !a.ended && a.paused && a.currentTime > 0 && a.duration > 0) {
      try { await a.play(); setState('playing'); return; } catch (e) { /* fall through to fetch */ }
    }
    // Verzeker een schone start
    cleanupAudio();

    setState('loading');
    try {
      const res = await axios.post(
        `${API}/tts`,
        { text, story_id: storyId, page_number: pageNumber },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audio.preload = 'auto';
      audioRef.current = audio;

      audio.onended = () => {
        setState('idle');
        // Parent informeren zodat auto-read door kan naar volgende pagina
        try { onEndedRef.current && onEndedRef.current(); } catch (e) { /* ignore */ }
      };
      audio.onpause = () => setState((s) => (s === 'playing' ? 'paused' : s));
      audio.onplay  = () => setState('playing');
      audio.onerror = () => { setState('error'); toast.error('Kon audio niet afspelen'); };

      await audio.play();
      setState('playing');
    } catch (err) {
      if (err && err.name === 'NotAllowedError') {
        // Browser blokkeerde autoplay (geen user gesture). Toon hint.
        setState('idle');
        toast.info('Tik op "Lees voor" om te starten');
        return;
      }
      if (err.response?.status === 402) {
        toast.info('🔊 Voorlezen is een premium-functie. Start je proefperiode!');
      } else if (err.response?.status === 429) {
        toast.warning('Dagelijkse limiet bereikt — morgen weer beschikbaar');
      } else {
        toast.error('Voorlezen niet beschikbaar');
      }
      setState('error');
    }
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  // AutoPlay (her)start zodra autoPlay aan staat of de pageNumber wisselt.
  // Dit voedt de keten "audio.onended → parent advance → nieuwe pageNumber →
  // dit effect → fetchAndPlay" zodat het verhaal vanzelf doorgelezen wordt.
  useEffect(() => {
    if (!autoPlay) return;
    if (!text || !isPremium) return;
    const t = setTimeout(() => { fetchAndPlay(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, pageNumber, storyId]);

  if (!text) return null;

  return (
    <button
      data-testid="voice-read-btn"
      onClick={state === 'playing' ? pause : fetchAndPlay}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 hover:bg-gold/25 text-gold text-xs font-bold transition-colors disabled:opacity-50"
      disabled={state === 'loading'}
      aria-label={state === 'playing' ? 'Pauzeer voorlezen' : 'Lees voor'}
    >
      {state === 'loading' ? (
        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Laden...</>
      ) : state === 'playing' ? (
        <><Pause className="w-3.5 h-3.5" /> Pauze</>
      ) : state === 'error' ? (
        <><VolumeX className="w-3.5 h-3.5" /> Probeer opnieuw</>
      ) : (
        <><Play className="w-3.5 h-3.5" /> {state === 'paused' ? 'Verder' : 'Lees voor'}</>
      )}
    </button>
  );
};

export default VoiceReadButton;
